import {useQuery} from "@tanstack/react-query";
import {
	DataTable,
	DataTableCellProps,
	useDataTableStorage,
	useSpringPage,
	useSpringPageable,
	useStorage,
} from "@variocube/app-ui";
import {DataTableColumn} from "@variocube/app-ui/src/data-table/DataTable";
import React, {useMemo} from "react";
import {Booking, BookingShareWithAppLink} from "../../../api";
import {useBookingShareApi} from "../../../api/bookingShare";
import {EmptyList} from "../../../controls";
import {useLocalization} from "@smalox/shared";
import {useCanManageBookingAccess, useTenantId} from "../../../tenant";
import {queryKeys} from "../../../utils/queryKeys";
import {BookingShareActionCell} from "./BookingShareActionCell";
import {BookingShareCreatedCell} from "./BookingShareCreatedCell";
import {BookingShareEmailsCell} from "./BookingShareEmailsCell";
import {BookingShareFilter} from "./BookingShareFilter";
import {BookingShareValidUntilCell} from "./BookingShareValidUntilCell";

export interface BookingShareList {
	booking: Booking;
}

const withBookingAccess =
	(canManageBookingAccess: boolean) =>
	(WrappedComponent: React.ComponentType<any>) =>
	(props: DataTableCellProps<BookingShareWithAppLink>) => (
		<WrappedComponent {...props} canManageBookingAccess={canManageBookingAccess} />
	);

const defaultFilter: BookingShareFilter = {status: "ACTIVE"};

export function BookingShareList({booking}: Readonly<BookingShareList>) {
	const {t} = useLocalization();

	const tenantId = useTenantId();
	const {queryBookingShares} = useBookingShareApi();
	const canManageBookingAccess = useCanManageBookingAccess(booking);

	const columns = useMemo(() =>
		[
			{
				label: t("bookings.shares.name"),
				field: "name",
				sortable: true,
				default: false,
			},
			{
				label: t("bookings.shares.created"),
				field: "created",
				sortable: true,
				default: true,
				component: BookingShareCreatedCell,
			},
			{
				label: t("bookings.shares.validUntil"),
				field: "validUntil",
				sortable: true,
				default: false,
				component: BookingShareValidUntilCell,
			},
			{
				label: t("bookings.shares.emails"),
				field: "emails",
				sortable: true,
				default: false,
				component: BookingShareEmailsCell,
			},
			{
				label: "",
				field: "",
				sortable: false,
				default: false,
				component: withBookingAccess(canManageBookingAccess)(BookingShareActionCell),
				align: "right",
			},
		] as ReadonlyArray<DataTableColumn<BookingShareWithAppLink>>, [t, canManageBookingAccess]);

	const {onPageChange, onSort, ...storage} = useDataTableStorage("BookingShare.paging", {
		sortField: "created",
		sortDirection: "asc",
	});
	const pageable = useSpringPageable(storage);

	const [filter, setFilter] = useStorage<BookingShareFilter>("BookingShare.filter", defaultFilter);

	const query = useMemo(() => ({
		...filter,
		...pageable,
	}), [filter, pageable]);

	const {data, isLoading, error} = useQuery(
		queryKeys.bookingShares.list(
			tenantId,
			booking.id,
			query,
		),
		() => queryBookingShares(booking.id, query),
		{
			staleTime: 60_000,
		},
	);

	const {rows, page} = useSpringPage(data);

	return (
		<>
			<BookingShareFilter value={filter} onChange={setFilter} />
			<DataTable
				columns={columns}
				rows={rows}
				page={page}
				loading={isLoading}
				error={error}
				onSort={onSort}
				onPageChange={onPageChange}
				empty={<EmptyList />}
				{...storage}
			/>
		</>
	);
}
